'use client';
import { useAtomValue } from 'jotai';
import { sizesContentTypeAtom, sizesComponentNameAtom } from '@/state/atoms';

import classNames from 'classnames';
import Image from 'next/image';
import useResponsiveProp from '@/hooks/useResponsiveProps';
import { type JSX, useMemo, useState, useCallback } from 'react';
import atMediaVariants from './at-media.variants';
import {
  EImageIntents,
  EImagePosition,
  type IAtMediaProps,
  type TMediaAspectRatio,
} from './at-media.types';
import {
  generateBlurPlaceholder,
  BLUR_PLACEHOLDERS,
} from './utils/blur-placeholder-generator';

function cleanUrl(rawUrl: string): string {
  if (!rawUrl || typeof rawUrl !== 'string') {
    throw new Error('Invalid URL provided');
  }

  return rawUrl
    .replace(/\\/g, '')
    .replace(/([^:]\/)\/+/g, '$1')
    .trim();
}

export const buildImageUrl = (
  src?: string,
  {
    width,
    height,
    quality = 70,
    format = 'webp',
  }: {
    width?: number;
    height?: number;
    quality?: number;
    format?: 'webp' | 'avif' | 'pjpg';
  } = {},
): string => {
  if (!src) return ''; // Return empty string if src is undefined or empty

  const cleanedSrc = cleanUrl(src);
  const isRelative = cleanedSrc.startsWith('/');

  const baseUrl =
    process.env.NEXT_PUBLIC_HOSTED_URL || 'https://www.schumacherhomes.com';

  const url = new URL(cleanedSrc, isRelative ? baseUrl : undefined);

  // Use format param if supported by the backend
  if (format) url.searchParams.set('format', format);

  // Optionally include width and height in pixels
  if (width) url.searchParams.set('width', width.toString());
  if (height) url.searchParams.set('height', height.toString());

  // Compression quality
  if (quality) url.searchParams.set('quality', quality.toString());

  return isRelative ? `${url.pathname}${url.search}` : url.toString();
};

/**
 * `AtMedia` is a responsive image component built on top of Next.js's `<Image />` component.
 * It handles CMS-driven image data, responsive layouts, loading states, alt text resolution,
 * and both optimized and unoptimized images based on asset source.
 *
 * This component is typically used for full-bleed visuals, decorative imagery,
 * media with overlays, or images rendered as background content.
 *
 * It supports:
 * - Responsive `src`, `alt`, `aspectRatio`, and `position` props
 * - Skeleton placeholder while loading
 * - Custom overlay rendering
 * - Intent-based role/alt accessibility
 * - Static vs dynamic URL handling for asset optimization
 */
export const AtMedia = ({
  src,
  alt,
  className,
  intent = EImageIntents.DEFAULT,
  roundedCorners = '3xl',
  sizes = '(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 33vw',
  position = { mobile: EImagePosition.CENTER, tablet: '', desktop: '' },
  priority = false,
  quality = 70,
  decorative = false,
  showOverlay = false,
  aspectRatio = { mobile: 'auto', tablet: '', desktop: '' },
  preload = false,
  figureClasses = '',
  blurTheme = 'light',
  ...rest
}: IAtMediaProps & {
  preload?: boolean;
  blurTheme?: 'light' | 'neutral' | 'warm' | 'custom';
  customBlurColor?: string;
}): JSX.Element => {
  const componentNameAtomVal = useAtomValue(sizesComponentNameAtom);
  const contentTypeAtomVal = useAtomValue(sizesContentTypeAtom);
  const sizesVal = componentNameAtomVal.sizes || contentTypeAtomVal.sizes;

  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const imageSrc = useResponsiveProp(src);
  const imagePosition = useResponsiveProp(position) as EImagePosition;

  const curAltText = useMemo(() => {
    return typeof alt === 'string'
      ? { mobile: alt, tablet: alt, desktop: alt }
      : alt;
  }, [alt]);

  const altText = useResponsiveProp(curAltText);
  const ar = useResponsiveProp(aspectRatio) as TMediaAspectRatio;

  const { base, container } = atMediaVariants({
    rounded: roundedCorners,
    intent,
    position: imagePosition,
    aspectRatio: ar,
  });

  const isBg = intent === EImageIntents.BACKGROUND;

  const imageUrl = useMemo(() => {
    try {
      return buildImageUrl(imageSrc, {
        quality,
        format: 'webp',
      });
    } catch (error) {
      console.error('Error building image URL:', error);
      return imageSrc;
    }
  }, [imageSrc, quality]);

  // Generate appropriate blur placeholder
  const blurDataURL = useMemo(() => {
    if (blurTheme === 'custom' && rest.customBlurColor) {
      return generateBlurPlaceholder(10, 6, rest.customBlurColor);
    }
    return BLUR_PLACEHOLDERS[blurTheme] || BLUR_PLACEHOLDERS.light;
  }, [blurTheme, rest.customBlurColor]);

  const handleLoad = useCallback(() => {
    setIsLoading(false);
    setHasError(false);
  }, []);

  const handleError = useCallback(() => {
    setIsLoading(false);
    setHasError(true);
    console.error('Failed to load image:', imageUrl);
  }, [imageUrl]);

  const accessibilityProps = useMemo(() => {
    if (isBg || decorative) {
      return {
        role: 'none' as const,
        alt: '',
        'aria-hidden': true,
      };
    }

    return {
      role: 'img' as const,
      alt: altText || 'Image',
    };
  }, [isBg, decorative, altText]);

  return (
    <figure
      className={classNames(container(), figureClasses)}
      aria-busy={isLoading}
      {...(hasError && { 'aria-label': 'Image failed to load' })}
    >
      {!hasError ? (
        <Image
          onLoad={handleLoad}
          onError={handleError}
          unoptimized={!imageUrl} // skip optimization for svg files
          data-testid="at-media"
          fetchPriority={priority ? 'high' : 'low'}
          src={
            imageUrl ||
            `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1' height='1'%3E%3Crect width='1' height='1' fill='%23ccc' /%3E%3C/svg%3E`
          }
          fill
          sizes={sizesVal || sizes}
          quality={quality}
          priority={priority}
          className={classNames(base(), className)}
          placeholder="blur"
          blurDataURL={blurDataURL}
          {...accessibilityProps}
          {...rest}
          alt={isBg || decorative ? '' : (altText ?? '')}
        />
      ) : (
        <div
          className={classNames(
            base(),
            className,
            'flex items-center justify-center bg-gray-100',
          )}
          role="img"
          aria-label="Image failed to load"
        >
          <span className="text-sm text-gray-400">Image unavailable</span>
        </div>
      )}

      {showOverlay && (
        <div
          className="absolute left-0 top-0 h-full w-full bg-modal-overlay"
          data-testid="at-media-overlay"
          aria-hidden="true"
        />
      )}
    </figure>
  );
};
